import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { celebrate } from "@/lib/confetti";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Check, Star, TrendingUp, TicketPercent, Sparkles, Users, Store } from "lucide-react";

function money(cents) { return `$${(cents / 100).toFixed(cents % 100 === 0 ? 0 : 2)}`; }

export default function Pricing() {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [plans, setPlans] = useState(null);
  const [busy, setBusy] = useState(false);
  const [interval, setInterval] = useState("monthly");
  const [promoCode, setPromoCode] = useState("");
  const [promoState, setPromoState] = useState({ status: "idle", info: null });

  // Which role's plans are we showing?
  // Default: URL param overrides, then user's role, then vendor.
  const initialRole = (searchParams.get("role") || user?.role || "vendor").toLowerCase() === "organizer" ? "organizer" : "vendor";
  const [role, setRole] = useState(initialRole);

  useEffect(() => {
    api.get("/plans").then(({ data }) => setPlans(data));
    if (user?.pending_promo_code) {
      setPromoCode(user.pending_promo_code);
      api.post("/promo-codes/validate", { code: user.pending_promo_code })
        .then(({ data }) => setPromoState(data?.valid ? { status: "valid", info: data } : { status: "idle", info: null }))
        .catch(() => {});
    }
  }, [user]);

  const checkPromo = async () => {
    const code = promoCode.trim();
    if (!code) { setPromoState({ status: "idle", info: null }); return; }
    setPromoState({ status: "checking", info: null });
    try {
      const { data } = await api.post("/promo-codes/validate", { code });
      setPromoState(data?.valid ? { status: "valid", info: data } : { status: "invalid", info: null });
    } catch { setPromoState({ status: "invalid", info: null }); }
  };

  const rolePlans = plans?.[role];
  const proPlan = rolePlans ? (interval === "monthly" ? rolePlans.pro_monthly : rolePlans.pro_yearly) : null;
  const proPlusPlan = rolePlans ? (interval === "monthly" ? rolePlans.pro_plus_monthly : rolePlans.pro_plus_yearly) : null;
  const freePlan = rolePlans?.free;
  const isPro = user?.plan === "pro";
  const isProPlus = user?.plan_tier === "pro_plus";
  const planKey = role === "organizer" ? "organizer_pro" : "maker_pro";
  const isVerifiedNonprofit = user?.nonprofit_status === "verified";
  // Effective price the user will actually pay at checkout, factoring in the
  // automatic 50% nonprofit discount. Shown as a struck-through original next
  // to the discounted price so the value is obvious.
  const effectivePriceCents = proPlan
    ? (isVerifiedNonprofit ? Math.round(proPlan.price_cents * 0.5) : proPlan.price_cents)
    : 0;

  // Role-aware cross-role wrong-role warning (a vendor trying to buy Organizer Pro
  // or vice-versa). Admin bypasses because they may want to test both flows.
  const wrongRoleForUpgrade = useMemo(() => {
    if (!user) return null;
    if (user.is_admin) return null;
    if (role === "organizer" && user.role !== "organizer")
      return "Organizer Pro is for event organizers. Switch to the Vendor tab for Maker Pro.";
    if (role === "vendor" && user.role !== "vendor")
      return "Maker Pro is for vendors. Switch to the Organizer tab for Organizer Pro.";
    return null;
  }, [user, role]);

  const upgrade = async (chosenInterval, plusTier = false) => {
    if (!user) {
      window.location.href = `/register?next=/pricing&role=${role}`;
      return;
    }
    if (wrongRoleForUpgrade) {
      toast.error(wrongRoleForUpgrade);
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/payments/subscribe", {
        origin_url: window.location.origin,
        interval: chosenInterval,
        plan: plusTier ? "maker_pro_plus" : planKey,
        promo_code: promoState.status === "valid" ? promoCode.trim() : undefined,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not start checkout");
    } finally { setBusy(false); }
  };

  const startTrial = async () => {
    // Both vendor and organizer trials share the same 7-day shape — endpoint
    // differs only in role. We hit the role-specific path so the backend
    // 403s if the user tries to open a trial they don't qualify for.
    if (!user) {
      window.location.href = `/register?next=/pricing&role=${role}`;
      return;
    }
    setBusy(true);
    try {
      const path = role === "organizer" ? "/organizer/start-trial" : "/vendor/start-trial";
      await api.post(path);
      celebrate({ particleCount: 120, scalar: 1.1 });
      toast.success(`Your 7-day ${role === "organizer" ? "Organizer" : "Maker"} Pro trial is live.`);
      // Let the confetti breathe for a beat before we reload — feels less
      // jarring than an immediate context swap.
      setTimeout(() => window.location.reload(), 900);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not start trial");
    } finally { setBusy(false); }
  };

  const openPortal = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/payments/portal", { origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not open billing portal");
    } finally { setBusy(false); }
  };

  if (!plans || !rolePlans) return <div className="p-20 text-center font-serif text-2xl">Loading…</div>;

  const proBrand = role === "organizer" ? "Organizer Pro" : "Maker Pro";
  const proAccent = role === "organizer" ? "var(--avt-orange)" : "var(--avt-purple)";

  return (
    <div className="mx-auto max-w-5xl px-6 lg:px-10 py-14" data-testid="pricing-page">
      <div className="overline" style={{ color: "var(--avt-pink)" }}>Pricing</div>
      <h1 className="font-serif text-5xl lg:text-6xl mt-2 leading-tight">
        <span style={{ color: "var(--avt-teal)" }}>Simple.</span>{" "}
        <span style={{ color: "var(--avt-purple)" }}>Fair.</span>{" "}
        <span style={{ color: "var(--avt-orange)" }}>Priced for real people.</span>
      </h1>
      <p className="mt-4 max-w-2xl text-base leading-relaxed" style={{ color: "var(--ink-soft)" }}>
        Free tiers on both sides. Upgrade to Pro when the AI features start paying you back.
      </p>

      {/* Role toggle */}
      <div className="mt-8 inline-flex rounded-full p-1 border" style={{ borderColor: "var(--border-warm)", backgroundColor: "var(--sand)" }} data-testid="pricing-role-toggle">
        {[
          { k: "vendor", label: "For vendors", Icon: Users, accent: "var(--avt-purple)" },
          { k: "organizer", label: "For organizers", Icon: Store, accent: "var(--avt-orange)" },
        ].map(({ k, label, Icon, accent }) => (
          <button
            key={k}
            data-testid={`pricing-role-${k}`}
            onClick={() => { setRole(k); setPromoState({ status: "idle", info: null }); }}
            className="px-5 py-2 text-xs uppercase tracking-widest rounded-full transition-all inline-flex items-center gap-1.5"
            style={{
              backgroundColor: role === k ? accent : "transparent",
              color: role === k ? "#fff" : "var(--ink-soft)",
            }}
          >
            <Icon className="h-3.5 w-3.5" />
            {label}
          </button>
        ))}
      </div>

      {/* Interval toggle */}
      <div className="mt-6 inline-flex rounded-full p-1 border ml-3" style={{ borderColor: "var(--border-warm)", backgroundColor: "var(--sand)" }}>
        {["monthly", "yearly"].map((k) => (
          <button
            key={k}
            data-testid={`pricing-interval-${k}`}
            onClick={() => setInterval(k)}
            className="px-5 py-2 text-xs uppercase tracking-widest rounded-full transition-all"
            style={{
              backgroundColor: interval === k ? "var(--ink)" : "transparent",
              color: interval === k ? "#fff" : "var(--ink-soft)",
            }}
          >
            {k === "yearly" ? `Yearly · save ${proPlan?.savings_vs_monthly_pct || (role === "organizer" ? 28 : 35)}%` : "Monthly"}
          </button>
        ))}
      </div>

      {/* Vendor-only price bump banner (kept — organizers didn't have a previous price so nothing to say) */}
      {role === "vendor" && proPlan.prev_price_cents && proPlan.prev_price_cents < proPlan.price_cents && (
        <div
          data-testid="price-bump-banner"
          className="mt-6 rounded-sm p-4 border-l-2 max-w-2xl"
          style={{
            borderLeftColor: "var(--avt-purple)",
            backgroundColor: "rgba(107,47,186,0.06)",
          }}
        >
          <div className="overline mb-1" style={{ color: "var(--avt-purple)" }}>
            <Sparkles className="h-3 w-3 inline mr-1" />
            Fair warning
          </div>
          <p className="text-sm leading-relaxed" style={{ color: "var(--ink)" }}>
            Maker Pro was <s style={{ color: "var(--ink-soft)" }}>{money(proPlan.prev_price_cents)}</s>{" "}
            <span>{money(proPlan.price_cents)}</span>/{proPlan.interval}. If you upgraded before the change, you keep your original price for life.
          </p>
        </div>
      )}

      <div className={`mt-8 grid ${role === "vendor" && proPlusPlan ? "md:grid-cols-3" : "md:grid-cols-2"} gap-6`}>
        {/* Free */}
        <div className="card-warm rounded-sm p-8" data-testid={`plan-card-${role}-free`}>
          <div className="overline" style={{ color: "var(--avt-lime)" }}>{freePlan.name}</div>
          <div className="mt-2 font-serif text-5xl">$0</div>
          <div className="text-xs" style={{ color: "var(--ink-soft)" }}>always</div>
          <ul className="mt-6 space-y-3 text-sm">
            {freePlan.features.map((f) => (
              <li key={f} className="flex items-start gap-2">
                <Check className="h-4 w-4 mt-0.5 flex-shrink-0" style={{ color: "var(--avt-lime)" }} /> {f}
              </li>
            ))}
          </ul>
          <div className="mt-8">
            {!user ? (
              <Link
                to={`/register?role=${role}`}
                data-testid={`plan-${role}-free-cta`}
                className="btn-outline w-full inline-flex items-center justify-center py-3 rounded-full"
              >
                Create free account
              </Link>
            ) : (
              <span className="text-xs" style={{ color: "var(--ink-soft)" }}>
                {isPro ? `You have ${proBrand} — free tier is always available.` : "You're on Free."}
              </span>
            )}
          </div>
        </div>

        {/* Pro */}
        <div className="card-warm rounded-sm p-8 relative overflow-hidden" data-testid={`plan-card-${role}-pro`} style={{ borderLeft: `3px solid ${proAccent}` }}>
          <div
            className="absolute top-4 right-4 inline-flex items-center gap-1 text-[10px] uppercase tracking-widest px-2 py-1 rounded-full"
            style={{ backgroundColor: "rgba(107,47,186,0.12)", color: proAccent }}
          >
            <Star className="h-3 w-3" /> {role === "organizer" ? "Popular with recurring markets" : "Most makers pick this"}
          </div>
          <div className="overline" style={{ color: proAccent }}>{proBrand}</div>
          <div className="mt-2 flex items-baseline gap-2 flex-wrap">
            <div className="font-serif text-5xl" data-testid={`plan-${role}-pro-price`}>
              {money(effectivePriceCents)}
            </div>
            <div className="text-sm" style={{ color: "var(--ink-soft)" }}>/{proPlan.interval}</div>
            {isVerifiedNonprofit && (
              <span
                data-testid={`plan-${role}-pro-price-nonprofit`}
                className="text-sm inline-flex items-center gap-1"
                style={{ color: "#4B7A0A" }}
              >
                <s style={{ color: "var(--ink-soft)" }}>{money(proPlan.price_cents)}</s>
                <span className="text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded"
                      style={{ backgroundColor: "rgba(132,204,22,0.14)", color: "#4B7A0A", fontWeight: 700 }}>
                  Nonprofit 50% off
                </span>
              </span>
            )}
            {!isVerifiedNonprofit && proPlan.prev_price_cents && proPlan.prev_price_cents < proPlan.price_cents && (
              <span
                data-testid={`plan-${role}-pro-price-was`}
                className="text-xs"
                style={{ color: "var(--ink-soft)" }}
              >
                was <s>{money(proPlan.prev_price_cents)}</s>
              </span>
            )}
          </div>
          <div className="text-xs" style={{ color: "var(--ink-soft)" }}>
            {interval === "yearly" ? `≈ ${money(Math.round(proPlan.price_cents / 12))} /mo · bill once a year` : "Cancel anytime"}
          </div>
          {role === "vendor" && user?.legacy_price && (
            <div
              data-testid="grandfathered-note"
              className="mt-3 rounded-sm px-3 py-2 text-xs"
              style={{
                backgroundColor: "rgba(107,47,186,0.10)",
                color: "var(--avt-purple)",
              }}
            >
              <Sparkles className="h-3 w-3 inline mr-1" />
              You&apos;re grandfathered — your invoice stays at the original price you signed up with.
            </div>
          )}
          <ul className="mt-6 space-y-3 text-sm">
            {proPlan.features.map((f) => (
              <li key={f} className="flex items-start gap-2">
                <Check className="h-4 w-4 mt-0.5 flex-shrink-0" style={{ color: proAccent }} /> {f}
              </li>
            ))}
          </ul>

          {/* Promo code — same UI for both roles; backend accepts it either way.
              We deliberately DON'T disable this when the user is already Pro:
              admins + curious users want to test codes without downgrading,
              and applying a code when you're already Pro is harmless (the
              upgrade CTA is hidden anyway). */}
          <div className="mt-6 pt-6 border-t" style={{ borderColor: "var(--border-warm)" }}>
            <label className="text-xs uppercase tracking-widest inline-flex items-center gap-1.5">
              <TicketPercent className="h-3 w-3" style={{ color: proAccent }} />
              Promo code <span className="lowercase" style={{ color: "var(--ink-soft)" }}>(optional)</span>
            </label>
            <div className="mt-2 flex gap-2">
              <Input
                data-testid="pricing-promo-code"
                value={promoCode}
                onChange={(e) => { setPromoCode(e.target.value); setPromoState({ status: "idle", info: null }); }}
                onBlur={checkPromo}
                placeholder="LAUNCH50"
                className="uppercase text-sm"
                disabled={busy}
              />
              <Button
                type="button"
                data-testid="pricing-promo-check"
                variant="outline"
                onClick={checkPromo}
                disabled={!promoCode || promoState.status === "checking" || busy}
                className="rounded-full whitespace-nowrap text-sm"
                size="sm"
              >
                {promoState.status === "checking" ? "…" : "Apply"}
              </Button>
            </div>
            {promoState.status === "valid" && promoState.info && (
              <div data-testid="pricing-promo-valid" className="mt-2 text-xs" style={{ color: "var(--avt-lime)" }}>
                ✓ <strong>{promoState.info.percent_off}% off</strong> first {interval === "monthly" ? "month" : "year"} —
                you&rsquo;ll pay <strong>{money(Math.round(proPlan.price_cents * (1 - promoState.info.percent_off / 100)))}</strong> at checkout.
              </div>
            )}
            {promoState.status === "invalid" && (
              <div data-testid="pricing-promo-invalid" className="mt-2 text-xs" style={{ color: "var(--avt-purple)" }}>
                Code not recognized.
              </div>
            )}
          </div>

          <div className="mt-6">
            {isPro && role === user?.role ? (
              <Button
                data-testid={`plan-${role}-pro-manage`}
                onClick={openPortal}
                disabled={busy}
                className="btn-primary w-full py-3 rounded-full inline-flex items-center justify-center gap-2"
              >
                Manage billing
              </Button>
            ) : user?.trial_available && user?.role === role ? (
              <>
                <Button
                  data-testid={`plan-${role}-pro-trial`}
                  onClick={startTrial}
                  disabled={busy}
                  className="btn-primary w-full py-3 rounded-full inline-flex items-center justify-center gap-2"
                >
                  <Sparkles className="h-4 w-4" />
                  {busy ? "Starting…" : "Start 7-day free trial"}
                </Button>
                <p className="mt-2 text-xs text-center" style={{ color: "var(--ink-soft)" }}>
                  No card required. Upgrades to {money(proPlan.price_cents)}/{proPlan.interval} after 7 days unless you cancel.
                </p>
              </>
            ) : user?.on_trial && user?.role === role ? (
              <>
                <Button
                  data-testid={`plan-${role}-pro-cta`}
                  onClick={() => upgrade(interval)}
                  disabled={busy || !!wrongRoleForUpgrade}
                  className="btn-primary w-full py-3 rounded-full inline-flex items-center justify-center gap-2"
                >
                  <TrendingUp className="h-4 w-4" />
                  {busy ? "Redirecting…" : `Upgrade now — ${money(proPlan.price_cents)}/${proPlan.interval}`}
                </Button>
                <p className="mt-2 text-xs text-center" style={{ color: "var(--avt-teal)" }}>
                  Trial active · {user.trial_days_remaining} day{user.trial_days_remaining === 1 ? "" : "s"} remaining
                </p>
              </>
            ) : (
              <Button
                data-testid={`plan-${role}-pro-cta`}
                onClick={() => upgrade(interval)}
                disabled={busy || !!wrongRoleForUpgrade}
                className="btn-primary w-full py-3 rounded-full inline-flex items-center justify-center gap-2"
              >
                <TrendingUp className="h-4 w-4" />
                {busy ? "Redirecting…" : `Upgrade — ${money(proPlan.price_cents)}/${proPlan.interval}`}
              </Button>
            )}
            {wrongRoleForUpgrade && (
              <p className="mt-2 text-xs text-center" style={{ color: "var(--avt-pink)" }}>
                {wrongRoleForUpgrade}
              </p>
            )}
          </div>
        </div>

        {/* Pro+ (vendor only) */}
        {role === "vendor" && proPlusPlan && (
          <div
            className="card-warm rounded-sm p-8 relative overflow-hidden"
            data-testid={`plan-card-${role}-pro-plus`}
            style={{ borderLeft: `3px solid var(--terracotta)` }}
          >
            <div
              className="absolute top-4 right-4 inline-flex items-center gap-1 text-[10px] uppercase tracking-widest px-2 py-1 rounded-full"
              style={{ backgroundColor: "rgba(107,47,186,0.14)", color: "var(--terracotta)" }}
            >
              <Sparkles className="h-3 w-3" /> Pro+ · Invoices + Contracts
            </div>
            <div className="overline" style={{ color: "var(--terracotta)" }}>Maker Pro+</div>
            <div className="mt-2 flex items-baseline gap-2 flex-wrap">
              <div className="font-serif text-5xl" data-testid={`plan-${role}-pro-plus-price`}>
                {money(proPlusPlan.price_cents)}
              </div>
              <div className="text-sm" style={{ color: "var(--ink-soft)" }}>/{proPlusPlan.interval}</div>
            </div>
            <div className="text-xs" style={{ color: "var(--ink-soft)" }}>
              {interval === "yearly"
                ? `≈ ${money(Math.round(proPlusPlan.price_cents / 12))} /mo · bill once a year`
                : "Cancel anytime"}
            </div>
            <ul className="mt-6 space-y-3 text-sm">
              {proPlusPlan.features.map((f) => (
                <li key={f} className="flex items-start gap-2">
                  <Check className="h-4 w-4 mt-0.5 flex-shrink-0" style={{ color: "var(--terracotta)" }} /> {f}
                </li>
              ))}
            </ul>
            <div className="mt-6">
              {isProPlus ? (
                <Button
                  data-testid={`plan-${role}-pro-plus-manage`}
                  onClick={openPortal}
                  disabled={busy}
                  className="btn-primary w-full py-3 rounded-full inline-flex items-center justify-center gap-2"
                >
                  Manage billing
                </Button>
              ) : (
                <Button
                  data-testid={`plan-${role}-pro-plus-cta`}
                  onClick={() => upgrade(interval, true)}
                  disabled={busy || !!wrongRoleForUpgrade}
                  className="btn-primary w-full py-3 rounded-full inline-flex items-center justify-center gap-2"
                >
                  <Sparkles className="h-4 w-4" />
                  {busy
                    ? "Redirecting…"
                    : isPro
                      ? `Upgrade to Pro+ — ${money(proPlusPlan.price_cents)}/${proPlusPlan.interval}`
                      : `Go Pro+ — ${money(proPlusPlan.price_cents)}/${proPlusPlan.interval}`}
                </Button>
              )}
            </div>
          </div>
        )}
      </div>

      <div className="mt-14 text-sm space-y-2 max-w-3xl" style={{ color: "var(--ink-soft)" }}>
        {role === "organizer" ? (
          <>
            <p><strong style={{ color: "var(--ink)" }}>Free organizers</strong> — post as many events as you like, keep 95% of every booth fee. We only make money when you do.</p>
            <p><strong style={{ color: "var(--ink)" }}>Organizer Pro</strong> — same 3% platform fee as Eventbrite Essentials, plus the AI planning suite. Any organizer running ≥ 3 mid-sized events per year comes out ahead vs. the free tier.</p>
          </>
        ) : (
          <>
            <p><strong style={{ color: "var(--ink)" }}>Grandfathered users</strong> — everyone who paid the original signup fee keeps Maker Pro for life. No action needed.</p>
            <p><strong style={{ color: "var(--ink)" }}>7-day free trial</strong> — new vendor signups get 7 days of Maker Pro automatically. No card required to start.</p>
          </>
        )}
        <p><strong style={{ color: "var(--ink)" }}>Refund policy</strong> — cancel any time, refunds pro-rated for the remainder of the billing period.</p>
      </div>
    </div>
  );
}
